import { useEffect, useRef, useState } from "react";

let typingAudio: AudioContext | null = null;
export function primeTypewriterSound() {
  if (typeof window === "undefined" || !window.AudioContext) return;
  typingAudio ??= new window.AudioContext();
  if (typingAudio.state === "suspended") void typingAudio.resume();
}

function playTypingTick(index: number) {
  if (!typingAudio || typingAudio.state !== "running") return;
  const now = typingAudio.currentTime;
  const oscillator = typingAudio.createOscillator();
  const gain = typingAudio.createGain();
  oscillator.type = "triangle";
  oscillator.frequency.value = index % 4 === 0 ? 760 : 620;
  gain.gain.setValueAtTime(0.003, now);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.018);
  oscillator.connect(gain).connect(typingAudio.destination);
  oscillator.start(now);
  oscillator.stop(now + 0.02);
}

export function TypewriterText({ text, speed = 20, className, sound = false, onComplete }: { text: string; speed?: number; className?: string; sound?: boolean; onComplete?: () => void }) {
  const reduceMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const [length, setLength] = useState(reduceMotion ? text.length : 0);
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;

  useEffect(() => {
    if (reduceMotion) { setLength(text.length); completeRef.current?.(); return; }
    setLength(0);
    let index = 0;
    let timer = 0;
    const typeNext = () => {
      index = Math.min(text.length, index + 1);
      setLength(index);
      if (sound && text[index - 1]?.trim()) playTypingTick(index);
      if (index >= text.length) { completeRef.current?.(); return; }
      const character = text[index - 1];
      const pause = character === "." || character === "?" || character === "!" ? speed * 5 : character === "," || character === ":" ? speed * 3 : character === "\n" ? speed * 4 : speed;
      timer = window.setTimeout(typeNext, pause);
    };
    timer = window.setTimeout(typeNext, speed);
    return () => window.clearTimeout(timer);
  }, [reduceMotion, sound, speed, text]);

  return <span className={`typewriter-stable${className ? ` ${className}` : ""}`} aria-label={text}>
    <span className="typewriter-placeholder" aria-hidden="true">{text}</span>
    <span className="typewriter-output" aria-hidden="true">{text.slice(0, length)}{length < text.length && <i className="typewriter-caret" />}</span>
  </span>;
}

export function TranscriptText({ text, pace = 165, delay = 0, className, onComplete }: { text: string; pace?: number; delay?: number; className?: string; onComplete?: () => void }) {
  const reduceMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const [visible, setVisible] = useState(reduceMotion);
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;

  useEffect(() => {
    if (reduceMotion) { setVisible(true); completeRef.current?.(); return; }
    setVisible(false);
    const showTimer = window.setTimeout(() => setVisible(true), delay);
    const completeTimer = window.setTimeout(() => completeRef.current?.(), delay + Math.max(280, Math.min(440, pace * 2)));
    return () => { window.clearTimeout(showTimer); window.clearTimeout(completeTimer); };
  }, [delay, pace, reduceMotion, text]);

  return <span className={`transcript-reveal${visible ? " is-visible" : ""}${className ? ` ${className}` : ""}`}>{text}</span>;
}
